import type { IconName } from '@kiips/ui';

/**
 * Review-scoped layer registry — parallel to `layer-registry.ts` (Plan).
 *
 * Plan layers control card overlays; review layers control which
 * SECTIONS render inside DetailsView (the Summary view). The two
 * registries are deliberately separate: a user who hides photos on
 * the Plan timeline (compact cards) might still want photos in the
 * Review summary, and vice versa.
 *
 * Adding a new review layer:
 *   1. Add an entry to REVIEW_LAYER_IDS below.
 *   2. Add an entry to REVIEW_LAYER_REGISTRY with metadata + default.
 *   3. Wrap the Review section in `useReviewLayer(id)`.
 *
 * See ADR-0040.
 */

export const REVIEW_LAYER_IDS = [
  'routeMap',
  'photos',
  'openingHours',
  'types',
  'notes',
  'checklists',
  'cost',
] as const;

export type ReviewLayerId = (typeof REVIEW_LAYER_IDS)[number];

export interface ReviewLayerMetadata {
  id: ReviewLayerId;
  label: string;
  icon: IconName;
  description: string;
  defaultOn: boolean;
  /** Layer is gated behind a paid subscription tier. See
   *  `LayerMetadata.proOnly` in `layer-registry.ts` for the contract. */
  proOnly?: boolean | undefined;
}

export const REVIEW_LAYER_REGISTRY: Record<ReviewLayerId, ReviewLayerMetadata> = {
  routeMap: {
    id: 'routeMap',
    label: 'Route map',
    icon: 'map',
    description: 'Per-day map preview',
    defaultOn: true,
  },
  photos: {
    id: 'photos',
    label: 'Photos',
    icon: 'photo',
    description: 'Place photos and gallery',
    defaultOn: true,
  },
  openingHours: {
    id: 'openingHours',
    label: 'Opening hours',
    icon: 'schedule',
    description: 'Hours block per place',
    defaultOn: true,
    proOnly: true,
  },
  types: {
    id: 'types',
    label: 'Place types',
    icon: 'label',
    description: 'Type chips per place',
    defaultOn: true,
  },
  notes: {
    id: 'notes',
    label: 'Notes',
    icon: 'sticky-note-2',
    description: 'Per-place + day notes',
    defaultOn: true,
  },
  checklists: {
    id: 'checklists',
    label: 'Checklists',
    icon: 'check-box',
    description: 'Per-day checklist summary',
    defaultOn: true,
  },
  cost: {
    id: 'cost',
    label: 'Cost',
    icon: 'payments',
    description: 'Price chips + per-day totals',
    defaultOn: true,
    proOnly: true,
  },
};

export function listReviewLayers(): ReviewLayerMetadata[] {
  return REVIEW_LAYER_IDS.map((id) => REVIEW_LAYER_REGISTRY[id]);
}

export function defaultReviewLayerPrefs(): Record<ReviewLayerId, boolean> {
  const out = {} as Record<ReviewLayerId, boolean>;
  for (const id of REVIEW_LAYER_IDS) out[id] = REVIEW_LAYER_REGISTRY[id].defaultOn;
  return out;
}
